import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Row,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import PageHelpLink from "../../../../Components/Common/PageHelpLink";
import { helpUrlForRoute } from "../../../../constants/helpRouteMap";
import {
  getReferral,
  ReferralDashboard,
} from "../../../../helpers/referral_helper";
import { isOwner } from "../../../../utils/tenantRoles";

const ReferralSettingsPage = () => {
  const { t, i18n } = useTranslation();
  const owner = isOwner();
  const [data, setData] = useState<ReferralDashboard | null>(null);
  const [loading, setLoading] = useState(owner);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    if (!owner) return;
    setLoading(true);
    setError(null);
    try {
      setData((await getReferral()).data);
    } catch (e: any) {
      setError(e?.message || t("referral.error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // A permissão é estável durante a sessão.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner]);

  const code = data?.referral_code || data?.code || "";
  const link = data?.referral_url || data?.link || "";
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success(t("referral.copied"));
    } catch {
      toast.error(t("referral.copyError"));
    }
  };

  document.title = t("referral.documentTitle");
  const helpHref = helpUrlForRoute("/settings/referral", i18n.language);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("referral.title")} actions={<PageHelpLink href={helpHref} label={t("common.help")} />} />
        <ToastContainer closeButton={false} limit={3} />
        {!owner ? <Alert color="warning">{t("permissions.ownerOnly")}</Alert> : loading ? (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        ) : error || !data ? (
          <Alert color="danger">{error || t("referral.error")} <Button color="link" size="sm" onClick={() => void load()}>{t("common.retry")}</Button></Alert>
        ) : (
          <Row className="justify-content-center"><Col lg={8}>
            <Alert color="success">
              <h5 className="alert-heading">{t("referral.reward.title")}</h5>
              <p className="mb-0">{t("referral.reward.help")}</p>
            </Alert>
            <Card>
              <CardBody>
                <p className="text-muted">{t("referral.subtitle")}</p>
                <div className="mb-3">
                  <span className="text-muted small d-block">{t("referral.code")}</span>
                  <strong className="fs-3">{code}</strong>
                </div>
                <div className="input-group mb-4">
                  <Input value={link} readOnly aria-label={t("referral.link")} />
                  <Button color="primary" onClick={() => void copy()}>{t("referral.copy")}</Button>
                </div>
                <Row>
                  <Col sm={6}><div className="border rounded p-3"><span className="text-muted">{t("referral.referred")}</span><strong className="fs-3 d-block">{data.referred_count}</strong></div></Col>
                  <Col sm={6}><div className="border rounded p-3"><span className="text-muted">{t("referral.rewards")}</span><strong className="fs-3 d-block">{data.rewards_granted}</strong></div></Col>
                </Row>
              </CardBody>
            </Card>
          </Col></Row>
        )}
      </Container>
    </div>
  );
};

export default ReferralSettingsPage;
